import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CartItemStateService } from '../../../services/cart-item-state.service';
import { ProductPeriodDto } from '../../../model/product';
import { LibModulesWolcModule } from '../../lib-modules-wolc.module';
import { BtOrderTimegridComponent } from '../order-timegrid/order-timegrid.component';

interface GuestCategory {
  idx: number;
  mk: string;
  max: number;
}

interface PeriodOption {
  value: string;  // "yyyy-MM-dd,yyyy-MM-dd"
  label: string;  // "yyyy-MM-dd - yyyy-MM-dd" or period name
}

@Component({
  selector: 'btlib-order-period-guests',
  standalone: true,
  imports: [LibModulesWolcModule, FormsModule, BtOrderTimegridComponent],
  templateUrl: './order-period-guests.component.html',
  styleUrl: './order-period-guests.component.scss'
})
export class BtOrderPeriodGuestsComponent {
  state = inject(CartItemStateService);

  product = this.state.product;
  period = this.state.period;
  guests = this.state.guests;
  schedule = this.state.schedule;
  timeItems = this.state.timeItems;
  selectableDates = this.state.selectableDates;

  // PERIOD = nedtreksboks med forhåndsdefinerte perioder.
  // DATE = kalender (range eller enkelt dato).
  // TIME = enkelt dato + tidsluke/timegrid.
  isPeriodProduct = computed(() => this.product()?.timeConfig === 'PERIOD');

  isRangeProduct = computed(() => {
    const p = this.product();
    if (p?.singleDateProduct) return false;
    if (this.isPeriodProduct()) return false;
    const tc = p?.timeConfig;
    if (tc === 'DATE') return true;
    if (tc === 'TIME') return false;
    // Fallback when timeConfig is null/NONE
    return !this.isTimeProduct();
  });

  isTimeProduct = computed(() => {
    const p = this.product();
    const tc = p?.timeConfig;
    if (tc === 'TIME') return true;
    if (tc === 'PERIOD' || tc === 'DATE') return false;
    // Fallback when timeConfig is null/NONE
    return this.timeItems()?.length > 0 || !!p?.orderTimeStart;
  });

  // For overnight products, check-in time is typically later than check-out
  // (e.g. 16:00 vs 10:00). When start > end, the range picker must be split
  // into two separate date pickers with different disabled-date logic.
  // Computed by backend with legacy fallbacks (orderTimeStart → checkInTime → 16:00 etc.)
  isStartBeforeEnd = computed(() => {
    const p = this.product();
    return p?.startBeforeEnd ?? true;
  });

  // Mirrors legacy pw-period-guests: day products without a minimum stay
  // (findMinDays == 0, not lodging) start with a single-date picker and a
  // "Velg flere datoer" link that switches to the range picker.
  showRangePicker = signal(false);

  showSingleDatePicker = computed(() => {
    const p = this.product();
    if (!this.isRangeProduct()) return false;
    if (this.showRangePicker()) return false;
    if ((p?.findMinDays ?? 0) !== 0 || p?.findTypeLodging) return false;
    // Legacy updateShowRangePicker: if the current period already spans
    // more than one day, show the range picker instead.
    const period = this.period();
    if (period.from && period.to && period.to > period.from) return false;
    return true;
  });

  // Start/end as Date objects for the split date pickers
  startDate = computed<Date | null>(() => {
    const from = this.period().from;
    return from ? this.isoToDate(from) : null;
  });

  endDate = computed<Date | null>(() => {
    const to = this.period().to;
    return to ? this.isoToDate(to) : null;
  });

  // Period options for PERIOD timeConfig — dropdown, not calendar.
  periodOptions = computed<PeriodOption[]>(() => {
    const periods = this.product()?.periods ?? [];
    return periods
      .filter(p => p.start != null && p.end != null)
      .map(p => ({
        value: `${this.epochToIso(p.start!)},${this.epochToIso(p.end!)}`,
        label: p.name || `${this.epochToIso(p.start!)} - ${this.epochToIso(p.end!)}`
      }));
  });

  selectedPeriod = computed<string | null>(() => {
    const p = this.period();
    if (!p.from || !p.to) return null;
    return `${p.from},${p.to}`;
  });

  timeOptions = computed(() => {
    const seen = new Set<string>();
    const options: { value: string; label: string }[] = [];
    for (const item of this.timeItems()) {
      if (item.startTime == null || item.endTime == null) continue;
      const start = this.minutesToTime(item.startTime);
      const end = this.minutesToTime(item.endTime);
      const value = `${start}-${end}`;
      if (seen.has(value)) continue;
      seen.add(value);
      const label = this.formatTimeLabel(start, end);
      options.push({ value, label });
    }
    return options;
  });

  selectedTime = computed(() => {
    const p = this.period();
    if (!p.startTime || !p.endTime) return null;
    return `${p.startTime}-${p.endTime}`;
  });

  selectedTimeLabel = computed(() => {
    const p = this.period();
    if (!p.startTime) return null;
    return this.formatTimeLabel(p.startTime, p.endTime ?? undefined);
  });

  // Build guest categories from product.mkGcs (message keys) and maxGc fields.
  // Only categories with a non-null mkGcs entry and maxGc > 0 are shown.
  guestCategories = computed<GuestCategory[]>(() => {
    const p = this.product();
    if (!p) return [];
    const mkGcs = p.mkGcs;
    const maxGuests = p.maxGuests ?? 100;
    const maxGc = [
      p.maxGc0 ?? maxGuests,
      p.maxGc1 ?? maxGuests,
      p.maxGc2 ?? maxGuests,
      p.maxGc3 ?? maxGuests,
      p.maxGc4 ?? maxGuests
    ];
    const cats: GuestCategory[] = [];
    for (let i = 0; i < 5; i++) {
      const mk = mkGcs?.[i];
      if (!mk) continue;
      const max = maxGc[i];
      if (max <= 0) continue;
      cats.push({ idx: i, mk, max });
    }
    // Fallback if no mkGcs from backend: show gc0 only
    if (cats.length === 0) {
      cats.push({ idx: 0, mk: 'bus.bookingItem.gc0', max: maxGc[0] > 0 ? maxGc[0] : maxGuests });
    }
    return cats;
  });

  // Compact label: if only one category, show the category name as the field label.
  guestsLabel = computed(() => {
    const cats = this.guestCategories();
    if (cats.length > 1) return 'app.lib.common.guests';
    return cats[0]?.mk ?? 'app.lib.common.guests';
  });

  async onDateRangeChange(dates: Date[] | null): Promise<void> {
    if (!dates || dates.length !== 2) return;
    const from = this.dateToIso(dates[0]);
    const to = this.dateToIso(dates[1]);
    await this.state.updatePeriod(from, to);
  }

  async onStartDateChange(date: Date | null): Promise<void> {
    if (!date) return;
    const from = this.dateToIso(date);
    const currentTo = this.period().to;

    if (!currentTo || from > currentTo) {
      // Start after current end (or no end set) — find first valid end date
      await this.resetEndDate(from, date);
    } else {
      // Start before current end — check if period is still valid
      const valid = await this.state.isPeriodOrderable(from, currentTo);
      if (valid) {
        await this.state.updatePeriod(from, currentTo);
      } else {
        // Period no longer valid (e.g. occupied dates in between) — reset end
        await this.resetEndDate(from, date);
      }
    }
  }

  private async resetEndDate(from: string, startDate: Date): Promise<void> {
    const validEnd = await this.state.findFirstValidEndDate(from);
    if (validEnd) {
      await this.state.updatePeriod(from, validEnd);
      return;
    }
    // Fallback: start + 1 day
    const next = new Date(startDate);
    next.setDate(next.getDate() + 1);
    await this.state.updatePeriod(from, this.dateToIso(next));
  }

  async onEndDateChange(date: Date | null): Promise<void> {
    if (!date) return;
    const to = this.dateToIso(date);
    const currentFrom = this.period().from;
    // If end is before current start, move start to end - 1 day
    if (currentFrom && to < currentFrom) {
      const prev = new Date(date);
      prev.setDate(prev.getDate() - 1);
      await this.state.updatePeriod(this.dateToIso(prev), to);
    } else {
      await this.state.updatePeriod(currentFrom ?? to, to);
    }
  }

  async onPeriodChange(value: string | null): Promise<void> {
    if (!value) return;
    const [from, to] = value.split(',');
    await this.state.updatePeriod(from || null, to || null);
  }

  async onSingleDateChange(date: Date | null): Promise<void> {
    if (!date) return;
    const from = this.dateToIso(date);
    await this.state.updatePeriod(from, from);
  }

  async onTimeChange(value: string | null): Promise<void> {
    if (!value) return;
    const [start, end] = value.split('-');
    await this.state.updateTime(start || null, end || null);
  }

  incrementGuest(idx: number): void {
    const counts = [...this.guests().counts];
    const cat = this.guestCategories().find(c => c.idx === idx);
    const max = cat?.max ?? 100;
    if (counts[idx] < max) {
      counts[idx] = (counts[idx] || 0) + 1;
      this.state.updateGuests(counts);
    }
  }

  decrementGuest(idx: number): void {
    const counts = [...this.guests().counts];
    if (counts[idx] > 0) {
      counts[idx] = counts[idx] - 1;
      this.state.updateGuests(counts);
    }
  }

  async onGuestInput(idx: number, value: number | null): Promise<void> {
    const counts = [...this.guests().counts];
    const cat = this.guestCategories().find(c => c.idx === idx);
    const max = cat?.max ?? 100;
    counts[idx] = Math.max(0, Math.min(value ?? 0, max));
    await this.state.updateGuests(counts);
  }

  // Disabled date for start picker — date must be in selectableDates
  disabledDate = (current: Date): boolean => {
    if (!current) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (current.getTime() < today.getTime()) return true;

    const dates = this.selectableDates();
    if (dates && dates.length > 0) {
      const currentInt = this.dateToInt(current);
      return dates.indexOf(currentInt) < 0;
    }
    return false;
  };

  // Disabled date for end picker — checkout day doesn't need to be available,
  // but the previous day (the night before checkout) must be available.
  // Also disables dates before the start date.
  pickerDisabledDateRangeEnd = (current: Date): boolean => {
    if (!current) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (current.getTime() < today.getTime()) return true;

    const from = this.period().from;
    if (from) {
      const start = this.isoToDate(from);
      if (start && current.getTime() < start.getTime()) return true;
    }

    // Previous day must be available as a start date
    const prev = new Date(current);
    prev.setDate(prev.getDate() - 1);
    if (this.disabledDate(prev)) return true;

    // All intermediate nights must be available
    if (from) {
      const start = this.isoToDate(from);
      if (start) {
        const dates = this.selectableDates();
        if (dates && dates.length > 0) {
          for (let d = new Date(start); d.getTime() < prev.getTime(); d.setDate(d.getDate() + 1)) {
            const dateInt = this.dateToInt(d);
            if (dates.indexOf(dateInt) < 0) return true;
          }
        }
      }
    }
    return false;
  };

  private dateToIso(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private epochToIso(epoch: number): string {
    const d = new Date(epoch);
    return this.dateToIso(d);
  }

  private dateToInt(date: Date): number {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return parseInt(`${year}${month}${day}`, 10);
  }

  private minutesToTime(minutes: number | null | undefined): string {
    if (minutes == null) return '';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  private isoToDate(iso: string): Date {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  private formatTimeLabel(start: string, end: string | null | undefined): string {
    const config = this.product()?.findTimeDisplayConfig ?? 'AUTO';
    if (config === 'SHOW_START_ONLY' || config === 'HIDE') {
      return start;
    }
    // AUTO / SHOW: show full range when end is present
    return end ? `${start}-${end}` : start;
  }
}
